import { createFileRoute, redirect, isRedirect } from "@tanstack/react-router";
import {
  getAdviceQueryOptions,
  getCustomerQueryOptions,
  getTransferPolicyQueryOptions,
} from "../../hooks/api";
import { Flex, Paper, Title } from "@mantine/core";
import NextStepsPaper from "../../components/nextStepsPaper";
import ContactUsPaper from "../../components/contactUsPaper";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import BookingConfirmationPaper from "../../components/bookingConfirmationPaper";
import ErrorComponent from "../../components/errorComponent";
import { LoaderComponent } from "../../components/loaderComponent";
import {
  getStoredCredentials,
  useUpdateCustomerData,
} from "../../hooks/ui/customerDataStore";
import PoaExpiredBanner from "../../components/poaValidation";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

export const Route = createFileRoute("/home/")({
  component: Home,
  loader: async ({ context }) => {
    const credentials = context.credentials?.sessionToken
      ? context.credentials
      : getStoredCredentials();

    if (!credentials?.sessionToken) {
      throw redirect({ to: "/login" });
    }

    try {
      const response = await Promise.all([
        context.queryClient.ensureQueryData(
          getCustomerQueryOptions(credentials),
        ),
        context.queryClient.ensureQueryData(
          getTransferPolicyQueryOptions(credentials),
        ),
        context.queryClient.ensureQueryData(getAdviceQueryOptions(credentials)),
      ]);

      const responseData = {
        customer: response[0],
        transfer: response[1],
        advice: response[2],
      };

      // Update store with customer data
      const { setCustomerData } = useUpdateCustomerData.getState();
      const customerData = responseData.customer[0];

      setCustomerData({
        email: customerData.user?.email || "",
        phoneNumber: customerData.user?.phone || "",
        firstName: customerData.user?.firstName || "",
        lastName: customerData.user?.lastName || "",
        personalNumber: customerData.user?.ssn || "",
        name: `${customerData.user?.firstName} ${customerData.user?.lastName}`,
        transactionId: customerData.transactionId || "",
        validFrom: customerData.validFrom || "",
        validTo: customerData.validTo || null,
        poa: customerData.poa || {},
        booking: customerData.booking || {},
        credentials: getStoredCredentials(),
        hasLockedAdvice: false,
      });

      if (response[0].length === 0) {
        throw redirect({ to: "/login" });
      }

      if (response[1]?.length > 0) {
        if (
          response[1].some((t: { status: string }) => t.status === "FINISHED")
        ) {
          throw redirect({ to: "/dashboard", search: true });
        }
        throw redirect({ to: "/transfer-status" });
      }

      if (
        response[2]?.hasAgreedAdvice &&
        response[2]?.insuranceAdvices?.length > 0
      ) {
        throw redirect({ to: "/advice-summary", search: true });
      }

      return {
        customer: response[0],
        transfer: response[1],
        advice: response[2],
      };
    } catch (err) {
      if (isRedirect(err)) throw err;
      //eslint-disable-next-line no-console
      console.error(err);
      throw redirect({ to: "/login", search: true });
    }
  },
  pendingComponent: () => <LoaderComponent />,
  errorComponent: () => <ErrorComponent />,
});

function Home() {
  //const routeApi = getRouteApi("/home/");
  //const { customer } = routeApi.useLoaderData();
  const { customerData } = useUpdateCustomerData();
  const firstName = capitalizeFirstLetter(customerData.firstName);

  return (
    <Flex direction="column">
      <PoaExpiredBanner />

      <Flex justify="center" align="center" mt={"2rem"}>
        <Flex direction="column">
          <Paper p={"xl"} radius={"24px"} mb={"xl"}>
            <Title
              order={1}
              size="h2"
              mb="1rem"
              ff="var(--wallet-font-family-sambla-sans)"
            >
              Hej <span className={REPLAY_BLOCK_CLASS}>{firstName}</span>!
            </Title>
            <BookingConfirmationPaper
              bookingId={customerData.booking.bookingId}
            />
          </Paper>
          <NextStepsPaper />
          <ContactUsPaper />
        </Flex>
      </Flex>
    </Flex>
  );
}
